import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { CheckCircle2, X } from 'lucide-react';
import { useConsultationModal, CONSULTATION_TYPES } from '../context/ConsultationModalContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Validation helpers
const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s\-'\.]+$/;
    return name.trim().length >= 2 && name.trim().length <= 50 && nameRegex.test(name.trim());
};

const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.trim().length <= 100 && emailRegex.test(email.trim());
};

const validatePhone = (phone) => {
    if (!phone.trim()) return true;
    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    return phone.trim().length >= 7 && phone.trim().length <= 20 && phoneRegex.test(phone.trim());
};

const validateCompany = (company) => {
    const companyRegex = /^[a-zA-Z0-9\s\-\.\,\&\(\)\']+$/;
    return company.trim().length >= 2 && company.trim().length <= 100 && companyRegex.test(company.trim());
};

const validateMessage = (message) => {
    if (!message.trim()) return false;
    return message.trim().length >= 10 && message.trim().length <= 2000;
};

// Sanitize input to prevent XSS
const sanitizeInput = (input) => {
    if (!input) return '';
    return input
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .trim();
};

const ConsultationModal = () => {
    const { isOpen, consultationType, closeModal } = useConsultationModal();
    const config = CONSULTATION_TYPES[consultationType] || CONSULTATION_TYPES.general;

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        industry: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    // Industry options
    const industryOptions = [
        { value: '', label: 'Select Industry' },
        { value: 'banking', label: 'Banking & Financial Services' },
        { value: 'government', label: 'Government & Public Sector' },
        { value: 'telecom', label: 'Telecom & ISP' },
        { value: 'healthcare', label: 'Healthcare' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'manufacturing', label: 'Manufacturing' },
        { value: 'retail', label: 'Retail & E-commerce' },
        { value: 'education', label: 'Education' },
        { value: 'other', label: 'Other' }
    ];

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
        // Clear error when user types
        if (errors[e.target.id]) {
            setErrors({ ...errors, [e.target.id]: '' });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!validateName(formData.firstName)) {
            newErrors.firstName = 'Please enter a valid first name (2-50 letters only)';
        }
        if (!validateName(formData.lastName)) {
            newErrors.lastName = 'Please enter a valid last name (2-50 letters only)';
        }
        if (!validateEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!validatePhone(formData.phone)) {
            newErrors.phone = 'Please enter a valid phone number (7-20 digits)';
        }
        if (!validateCompany(formData.company)) {
            newErrors.company = 'Please enter a valid company name (2-100 characters)';
        }
        if (!validateMessage(formData.message)) {
            newErrors.message = 'Please enter a message (10-2000 characters)';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error("Please fix the errors in the form");
            return;
        }
        
        setIsSubmitting(true);
        try {
            const payload = {
                consultation_type: consultationType,
                ...formData
            };
            await axios.post(`${API}/consultation`, payload);
            toast.success("Request submitted successfully!");
            setIsSubmitted(true);
            setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', industry: '', message: '' });
            setErrors({});
        } catch (error) {
            console.error(error);
            toast.error("Failed to send request. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsSubmitted(false);
        setErrors({});
        setFormData({ firstName: '', lastName: '', email: '', phone: '', company: '', industry: '', message: '' });
        closeModal();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[550px] bg-white dark:bg-slate-900 border-slate-200 dark:border-white/10 p-0 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-accent/10 to-transparent px-6 py-5 border-b border-slate-200 dark:border-white/5">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold text-slate-900 dark:text-white">
                            {config.title}
                        </DialogTitle>
                        <DialogDescription className="text-accent font-medium text-sm uppercase tracking-wider">
                            {config.subtitle}
                        </DialogDescription>
                    </DialogHeader>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                    {isSubmitted ? (
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Request Submitted!</h3>
                            <p className="text-slate-600 dark:text-slate-400 mb-6 text-sm">
                                Our team will contact you shortly at the email provided.
                            </p>
                            <Button 
                                onClick={handleClose}
                                className="bg-accent text-black hover:bg-accent/90 font-bold"
                            >
                                Close
                            </Button>
                        </div>
                    ) : (
                        <>
                            <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
                                {config.description}
                            </p>
                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="consultation-modal-form">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="firstName" className="text-slate-600 dark:text-slate-400 text-sm">First Name</Label>
                                        <Input 
                                            id="firstName" 
                                            placeholder="Rajesh" 
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            maxLength={50}
                                            className={`bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-white/10 h-10 ${errors.firstName ? 'border-red-400' : ''}`}
                                            required
                                            data-testid="modal-input-firstName"
                                        />
                                        {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="lastName" className="text-slate-600 dark:text-slate-400 text-sm">Last Name</Label>
                                        <Input 
                                            id="lastName" 
                                            placeholder="Sharma" 
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            maxLength={50}
                                            className={`bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-white/10 h-10 ${errors.lastName ? 'border-red-400' : ''}`}
                                            required
                                            data-testid="modal-input-lastName"
                                        />
                                        {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="email" className="text-slate-600 dark:text-slate-400 text-sm">Work Email</Label>
                                        <Input 
                                            id="email" 
                                            type="email" 
                                            placeholder="rajesh@company.com.np" 
                                            value={formData.email}
                                            onChange={handleChange}
                                            maxLength={100}
                                            className={`bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-white/10 h-10 ${errors.email ? 'border-red-400' : ''}`}
                                            required
                                            data-testid="modal-input-email"
                                        />
                                        {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="phone" className="text-slate-600 dark:text-slate-400 text-sm">Phone Number</Label>
                                        <Input 
                                            id="phone" 
                                            type="tel" 
                                            placeholder="+977-98XXXXXXXX" 
                                            value={formData.phone}
                                            onChange={handleChange}
                                            maxLength={20}
                                            className={`bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-white/10 h-10 ${errors.phone ? 'border-red-400' : ''}`}
                                            data-testid="modal-input-phone"
                                        />
                                        {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="company" className="text-slate-600 dark:text-slate-400 text-sm">Company / Organization</Label>
                                        <Input 
                                            id="company" 
                                            placeholder="Nepal Commercial Bank Limited" 
                                            value={formData.company}
                                            onChange={handleChange}
                                            maxLength={100}
                                            className={`bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-white/10 h-10 ${errors.company ? 'border-red-400' : ''}`}
                                            required
                                            data-testid="modal-input-company"
                                        />
                                        {errors.company && <p className="text-xs text-red-500">{errors.company}</p>}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="industry" className="text-slate-600 dark:text-slate-400 text-sm">Industry</Label>
                                        <select
                                            id="industry"
                                            value={formData.industry}
                                            onChange={handleChange}
                                            className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white rounded-md text-sm focus:outline-none"
                                            data-testid="modal-select-industry"
                                        >
                                            {industryOptions.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="message" className="text-slate-600 dark:text-slate-400 text-sm">How can we help?</Label>
                                    <Textarea 
                                        id="message" 
                                        placeholder="Tell us about your cloud infrastructure requirements..."
                                        className={`min-h-[100px] bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-white/10 resize-none ${errors.message ? 'border-red-400' : ''}`}
                                        value={formData.message}
                                        onChange={handleChange}
                                        maxLength={2000}
                                        required
                                        data-testid="modal-textarea-message"
                                    />
                                    {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
                                </div>
                                <Button 
                                    type="submit" 
                                    className="w-full bg-accent text-black hover:bg-accent/90 h-11 font-bold rounded-full mt-2" 
                                    disabled={isSubmitting}
                                    data-testid="modal-submit-button"
                                >
                                    {isSubmitting ? 'Submitting...' : config.buttonText}
                                </Button>
                                <p className="text-xs text-slate-500 dark:text-slate-500 text-center">
                                    All inquiries are treated as confidential.
                                </p>
                            </form>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ConsultationModal;
